// src/components/PropertyForm/PropertyForm.js
import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Divider,
  Chip,
  alpha,
  useTheme,
  Collapse,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import {
  CloudUploadRounded,
  DeleteRounded,
  SaveRounded,
  CancelRounded,
  CheckCircleRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  PoolRounded,
  YardRounded,
  BalconyRounded,
  KingBedRounded,
  CheckroomRounded,
  AcUnitRounded,
  ChairRounded,
  OutdoorGrillRounded,
  FitnessCenterRounded,
  SecurityRounded,
  CelebrationRounded,
  ChildCareRounded,
  LaptopRounded,
  RestaurantRounded,
  NewReleasesRounded,
  ElevatorRounded,
  SolarPowerRounded,
  PetsRounded,
  AccessibleRounded
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { propertiesAPI } from '../../services/api'
import LocationPicker from '../LocationPicker/LocationPicker'

// Definição das características especiais com ícones
const SPECIAL_FEATURES = [
  { key: 'has_pool', label: 'Piscina', icon: PoolRounded },
  { key: 'has_garden', label: 'Jardim', icon: YardRounded },
  { key: 'has_balcony', label: 'Sacada / Varanda', icon: BalconyRounded },
  { key: 'has_suite', label: 'Suíte', icon: KingBedRounded },
  { key: 'has_closet', label: 'Closet', icon: CheckroomRounded },
  {
    key: 'has_air_conditioning',
    label: 'Ar-condicionado',
    icon: AcUnitRounded
  },
  { key: 'is_furnished', label: 'Mobiliado', icon: ChairRounded },
  { key: 'has_grill', label: 'Churrasqueira', icon: OutdoorGrillRounded },
  { key: 'has_gym', label: 'Academia', icon: FitnessCenterRounded },
  { key: 'has_24h_security', label: 'Portaria 24h', icon: SecurityRounded },
  { key: 'has_party_room', label: 'Salão de festas', icon: CelebrationRounded },
  { key: 'has_playground', label: 'Playground', icon: ChildCareRounded },
  { key: 'has_coworking', label: 'Coworking', icon: LaptopRounded },
  { key: 'has_gourmet_area', label: 'Espaço gourmet', icon: RestaurantRounded },
  { key: 'is_new', label: 'Imóvel novo', icon: NewReleasesRounded },
  { key: 'has_elevator', label: 'Elevador', icon: ElevatorRounded },
  { key: 'has_solar_energy', label: 'Energia solar', icon: SolarPowerRounded },
  { key: 'is_pet_friendly', label: 'Pet-friendly', icon: PetsRounded },
  { key: 'is_accessible', label: 'Acessível para PCD', icon: AccessibleRounded }
]

const PropertyForm = ({ property = null, onSave, onCancel }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isEditing = !!property

  // Funções auxiliares para formatação de números
  const formatNumber = (value) => {
    if (!value) return ''
    const number = value.toString().replace(/\D/g, '')
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

  const parseFormattedNumber = (value) => {
    if (!value) return ''
    return value.toString().replace(/\./g, '')
  }

  const [formData, setFormData] = useState({
    title: property?.title || '',
    description: property?.description || '',
    property_type: property?.property_type || 'casa',
    transaction_type: property?.transaction_type || 'venda',
    price: property?.price || '',
    price_on_request: property?.price_on_request || false,
    area: property?.area || '',
    bedrooms: property?.bedrooms ?? 0,
    bathrooms: property?.bathrooms ?? 0,
    garage_spaces: property?.garage_spaces ?? 0,
    street: property?.street || '',
    number: property?.number || '',
    complement: property?.complement || '',
    neighborhood: property?.neighborhood || '',
    city: property?.city || '',
    state: property?.state || '',
    zipcode: property?.zipcode || '',
    latitude: property?.latitude || '',
    longitude: property?.longitude || '',
    // Características especiais
    has_pool: property?.has_pool || false,
    has_garden: property?.has_garden || false,
    has_balcony: property?.has_balcony || false,
    has_suite: property?.has_suite || false,
    has_closet: property?.has_closet || false,
    has_air_conditioning: property?.has_air_conditioning || false,
    is_furnished: property?.is_furnished || false,
    has_grill: property?.has_grill || false,
    has_gym: property?.has_gym || false,
    has_24h_security: property?.has_24h_security || false,
    has_party_room: property?.has_party_room || false,
    has_playground: property?.has_playground || false,
    has_coworking: property?.has_coworking || false,
    has_gourmet_area: property?.has_gourmet_area || false,
    is_new: property?.is_new || false,
    has_elevator: property?.has_elevator || false,
    has_solar_energy: property?.has_solar_energy || false,
    is_pet_friendly: property?.is_pet_friendly || false,
    is_accessible: property?.is_accessible || false,
    is_active: property?.is_active ?? true
  })

  const [images, setImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState(property?.images || [])
  const [loading, setLoading] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)
  const [error, setError] = useState('')
  const [showAllFeatures, setShowAllFeatures] = useState(false)
  const [activeStep, setActiveStep] = useState(0)

  // Estados para valores formatados
  const [priceFormatted, setPriceFormatted] = useState(
    property?.price ? formatNumber(property.price) : ''
  )
  const [areaFormatted, setAreaFormatted] = useState(
    property?.area ? formatNumber(property.area) : ''
  )

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePriceChange = (e) => {
    const formatted = formatNumber(e.target.value)
    setPriceFormatted(formatted)
    const rawValue = parseFormattedNumber(formatted)
    handleInputChange('price', rawValue)
  }

  const handleAreaChange = (e) => {
    const formatted = formatNumber(e.target.value)
    setAreaFormatted(formatted)
    const rawValue = parseFormattedNumber(formatted)
    handleInputChange('area', rawValue)
  }

  // Máscara de CEP: 00000-000
  const maskCEP = (value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1')
  }

  const handleCepChange = async (e) => {
    const maskedValue = maskCEP(e.target.value)
    handleInputChange('zipcode', maskedValue)

    // Buscar endereço quando CEP estiver completo
    const cepNumbers = maskedValue.replace(/\D/g, '')
    if (cepNumbers.length === 8) {
      await fetchAddressByCep(cepNumbers)
    }
  }

  const fetchAddressByCep = async (cep) => {
    setLoadingCep(true)
    setError('')

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const data = await response.json()

      if (data.erro) {
        setError('CEP não encontrado')
        setLoadingCep(false)
        return
      }

      // Preencher campos automaticamente
      setFormData((prev) => ({
        ...prev,
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || prev.complement
      }))

      // Buscar coordenadas usando Nominatim (OpenStreetMap)
      await fetchCoordinates(data.logradouro, data.localidade, data.uf)
    } catch (error) {
      console.error('Erro ao buscar CEP:', error)
      setError('Erro ao buscar informações do CEP')
    } finally {
      setLoadingCep(false)
    }
  }

  const fetchCoordinates = async (street, city, state) => {
    try {
      const address = `${street}, ${city}, ${state}, Brasil`
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`
      )
      const data = await response.json()

      if (data && data.length > 0) {
        setFormData((prev) => ({
          ...prev,
          latitude: data[0].lat,
          longitude: data[0].lon
        }))
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error)
      // Não exibir erro pois coordenadas são opcionais
    }
  }

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    // Verificar se não ultrapassa 12 imagens
    if (imagePreviews.length + files.length > 12) {
      setError(
        `Máximo de 12 imagens permitidas. Você já tem ${imagePreviews.length} imagens.`
      )
      return
    }

    const validFiles = files.filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        setError('Arquivos devem ter no máximo 5MB')
        return false
      }
      if (!file.type.startsWith('image/')) {
        setError('Apenas imagens são permitidas')
        return false
      }
      return true
    })

    setImages((prev) => [...prev, ...validFiles])

    validFiles.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreviews((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            image: e.target.result,
            is_main: prev.length === 0,
            file: file
          }
        ])
      }
      reader.readAsDataURL(file)
    })

    setError('')
  }

  const handleRemoveImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
    setImages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSetMainImage = (index) => {
    setImagePreviews((prev) =>
      prev.map((img, i) => ({
        ...img,
        is_main: i === index
      }))
    )
  }

  const steps = [
    'Informações Básicas',
    'Valores e Medidas',
    'Localização',
    'Características',
    'Imagens'
  ]

  const validateForm = () => {
    const errors = []

    if (!formData.title.trim()) errors.push('Título é obrigatório')
    if (!formData.description.trim()) errors.push('Descrição é obrigatória')
    if (!formData.price_on_request && (!formData.price || formData.price <= 0))
      errors.push('Preço deve ser maior que zero ou marque "Valor a Combinar"')
    if (!formData.area || formData.area <= 0)
      errors.push('Área deve ser maior que zero')
    if (!formData.city.trim()) errors.push('Cidade é obrigatória')
    if (!formData.state.trim()) errors.push('Estado é obrigatório')

    // Validação de mínimo 1 imagem e máximo 12
    const totalImages = imagePreviews.length
    if (totalImages < 1) {
      errors.push(`Adicione pelo menos 1 imagem (você tem ${totalImages})`)
    }
    if (totalImages > 12) {
      errors.push(`Máximo de 12 imagens permitidas (você tem ${totalImages})`)
    }

    return errors
  }

  const validateStep = (step) => {
    const errors = []

    switch (step) {
      case 0: // Informações Básicas
        if (!formData.title.trim()) errors.push('Título é obrigatório')
        if (!formData.description.trim()) errors.push('Descrição é obrigatória')
        break
      case 1: // Valores e Medidas
        if (!formData.price_on_request && (!formData.price || formData.price <= 0))
          errors.push('Preço deve ser maior que zero ou marque "Valor a Combinar"')
        if (!formData.area || formData.area <= 0)
          errors.push('Área deve ser maior que zero')
        break
      case 2: // Localização
        if (!formData.city.trim()) errors.push('Cidade é obrigatória')
        if (!formData.state.trim()) errors.push('Estado é obrigatório')
        break
      case 3: // Características
        // Sem validações obrigatórias
        break
      case 4: // Imagens
        const totalImages = imagePreviews.length
        if (totalImages < 6) {
          errors.push(`Adicione pelo menos 6 imagens (você tem ${totalImages})`)
        }
        if (totalImages > 12) {
          errors.push(
            `Máximo de 12 imagens permitidas (você tem ${totalImages})`
          )
        }
        break
      default:
        break
    }

    return errors
  }

  const handleNext = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    const errors = validateStep(activeStep)
    if (errors.length > 0) {
      setError(errors.join('\n'))
      return
    }
    setError('')
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setError('')
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validateForm()
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const submitData = new FormData()

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key])
        }
      })

      // Adicionar novas imagens
      images.forEach((file) => {
        submitData.append('uploaded_images', file)
      })

      // Se estiver editando, enviar IDs das imagens existentes que devem ser mantidas
      if (isEditing) {
        const existingImageIds = imagePreviews
          .filter((img) => img.id && !img.file) // Imagens que já existem no backend
          .map((img) => img.id)

        existingImageIds.forEach((id) => {
          submitData.append('existing_images', id)
        })
      }

      let response
      if (isEditing) {
        response = await propertiesAPI.update(property.id, submitData)
      } else {
        response = await propertiesAPI.create(submitData)
      }

      if (onSave) {
        onSave(response.data)
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      console.error('Erro ao salvar imóvel:', error)
      setError('Erro ao salvar imóvel. Verifique os campos e tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4
        }}
      >
        <Typography variant='h4' fontWeight='bold' color='primary.main'>
          {isEditing ? 'Editar Imóvel' : 'Adicionar Novo Imóvel'}
        </Typography>
        <Chip
          label={isEditing ? 'Modo Edição' : 'Novo Anúncio'}
          color={isEditing ? 'primary' : 'success'}
          sx={{ fontWeight: 'bold', px: 1 }}
        />
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }} variant='filled'>
          {error.split('\n').map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </Alert>
      )}

      <Box component='form' onSubmit={handleSubmit}>
        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Grid container spacing={3} direction='column'>
          {/* Step 0: Informações Básicas */}
          {activeStep === 0 && (
            <>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  color='text.primary'
                  gutterBottom
                >
                  📝 Informações Básicas
                </Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  required
                  fullWidth
                  label='Título do Anúncio'
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder='Ex: Apartamento 3 quartos no centro'
                  helperText='Crie um título atrativo para seu anúncio'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label='Descrição Detalhada'
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  placeholder='Descreva as principais características, diferenciais e o que torna este imóvel especial...'
                  helperText='Quanto mais detalhes, melhor!'
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Imóvel</InputLabel>
                  <Select
                    value={formData.property_type}
                    label='Tipo de Imóvel'
                    onChange={(e) =>
                      handleInputChange('property_type', e.target.value)
                    }
                  >
                    <MenuItem value='casa'>🏠 Casa</MenuItem>
                    <MenuItem value='apartamento'>🏢 Apartamento</MenuItem>
                    <MenuItem value='terreno'>🌳 Terreno</MenuItem>
                    <MenuItem value='comercial'>🏪 Comercial</MenuItem>
                    <MenuItem value='rural'>🌾 Rural</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Transação</InputLabel>
                  <Select
                    value={formData.transaction_type}
                    label='Tipo de Transação'
                    onChange={(e) =>
                      handleInputChange('transaction_type', e.target.value)
                    }
                  >
                    <MenuItem value='venda'>💰 Venda</MenuItem>
                    <MenuItem value='aluguel'>🔑 Aluguel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Step 1: Valores e Medidas */}
          {activeStep === 1 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  color='text.primary'
                  gutterBottom
                >
                  💰 Valores e Medidas
                </Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  required={!formData.price_on_request}
                  disabled={formData.price_on_request}
                  fullWidth
                  label='Preço (R$)'
                  value={formData.price_on_request ? '' : priceFormatted}
                  onChange={handlePriceChange}
                  placeholder={formData.price_on_request ? 'Valor a Combinar' : 'Ex: 500.000'}
                  helperText='Valor será formatado automaticamente'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.price_on_request}
                      onChange={(e) => {
                        handleInputChange('price_on_request', e.target.checked)
                        if (e.target.checked) {
                          setPriceFormatted('')
                          handleInputChange('price', '')
                        }
                      }}
                    />
                  }
                  label='Valor a Combinar'
                />
                <TextField
                  required
                  fullWidth
                  label='Área Total (m²)'
                  value={areaFormatted}
                  onChange={handleAreaChange}
                  placeholder='Ex: 150'
                  helperText='Área em metros quadrados'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  sx={{ mb: 1, mt: 1 }}
                >
                  Composição do Imóvel
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  type='number'
                  label='Quartos'
                  value={formData.bedrooms}
                  onChange={(e) =>
                    handleInputChange('bedrooms', parseInt(e.target.value))
                  }
                  inputProps={{ min: 0 }}
                />
                <TextField
                  required
                  fullWidth
                  type='number'
                  label='Banheiros'
                  value={formData.bathrooms}
                  onChange={(e) =>
                    handleInputChange('bathrooms', parseInt(e.target.value))
                  }
                  inputProps={{ min: 0 }}
                />
                <TextField
                  fullWidth
                  type='number'
                  label='Vagas de Garagem'
                  value={formData.garage_spaces}
                  onChange={(e) =>
                    handleInputChange('garage_spaces', parseInt(e.target.value))
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </>
          )}

          {/* Step 2: Localização */}
          {activeStep === 2 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  color='text.primary'
                  gutterBottom
                >
                  📍 Localização
                </Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label='CEP'
                  value={formData.zipcode}
                  onChange={handleCepChange}
                  placeholder='00000-000'
                  helperText={
                    loadingCep
                      ? 'Buscando endereço...'
                      : 'Digite o CEP para preencher automaticamente'
                  }
                  disabled={loadingCep}
                  InputProps={{
                    endAdornment: loadingCep && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', pr: 1 }}
                      >
                        <Typography variant='caption' color='primary'>
                          Buscando...
                        </Typography>
                      </Box>
                    )
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label='Rua / Avenida'
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  placeholder='Ex: Rua das Flores'
                  disabled={loadingCep}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label='Número'
                  value={formData.number}
                  onChange={(e) => handleInputChange('number', e.target.value)}
                  placeholder='Ex: 123'
                  helperText='Digite o número do imóvel'
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label='Complemento'
                  value={formData.complement}
                  onChange={(e) =>
                    handleInputChange('complement', e.target.value)
                  }
                  placeholder='Ex: Apto 45, Bloco B'
                  helperText='Apartamento, bloco, casa (opcional)'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label='Bairro'
                  value={formData.neighborhood}
                  onChange={(e) =>
                    handleInputChange('neighborhood', e.target.value)
                  }
                  placeholder='Ex: Centro'
                  disabled={loadingCep}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }} sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  label='Cidade'
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder='Ex: São Paulo'
                  disabled={loadingCep}
                />
                <TextField
                  required
                  fullWidth
                  label='Estado (UF)'
                  value={formData.state}
                  onChange={(e) =>
                    handleInputChange('state', e.target.value.toUpperCase())
                  }
                  placeholder='Ex: SP'
                  disabled={loadingCep}
                  inputProps={{
                    maxLength: 2,
                    style: { textTransform: 'uppercase' }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  sx={{ mb: 1, mt: 2 }}
                >
                  Localize o imóvel no mapa (opcional, mas recomendado)
                </Typography>
                <LocationPicker
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  street={formData.street}
                  city={formData.city}
                  state={formData.state}
                  onLocationChange={(coords) => {
                    setFormData((prev) => ({
                      ...prev,
                      latitude: coords.latitude,
                      longitude: coords.longitude
                    }))
                  }}
                />
              </Grid>
            </>
          )}

          {/* Step 3: Características Especiais */}
          {activeStep === 3 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  color='text.primary'
                  gutterBottom
                >
                  ✨ Características Especiais
                </Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: 2 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack spacing={2}>
                  {/* Principais características (sempre visíveis) */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {SPECIAL_FEATURES.slice(0, 6).map((feature) => {
                      const IconComponent = feature.icon
                      return (
                        <Chip
                          key={feature.key}
                          icon={<IconComponent />}
                          label={feature.label}
                          onClick={() =>
                            handleInputChange(
                              feature.key,
                              !formData[feature.key]
                            )
                          }
                          color={formData[feature.key] ? 'primary' : 'default'}
                          variant={
                            formData[feature.key] ? 'filled' : 'outlined'
                          }
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: formData[feature.key]
                                ? alpha(theme.palette.primary.main, 0.8)
                                : alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        />
                      )
                    })}
                  </Box>

                  {/* Botão para expandir/recolher mais opções */}
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => setShowAllFeatures(!showAllFeatures)}
                    endIcon={
                      showAllFeatures ? (
                        <ExpandLessRounded />
                      ) : (
                        <ExpandMoreRounded />
                      )
                    }
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {showAllFeatures ? 'Ver menos opções' : 'Ver mais opções'}
                  </Button>

                  {/* Características adicionais (expansível) */}
                  <Collapse in={showAllFeatures}>
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}
                    >
                      {SPECIAL_FEATURES.slice(6).map((feature) => {
                        const IconComponent = feature.icon
                        return (
                          <Chip
                            key={feature.key}
                            icon={<IconComponent />}
                            label={feature.label}
                            onClick={() =>
                              handleInputChange(
                                feature.key,
                                !formData[feature.key]
                              )
                            }
                            color={
                              formData[feature.key] ? 'primary' : 'default'
                            }
                            variant={
                              formData[feature.key] ? 'filled' : 'outlined'
                            }
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: formData[feature.key]
                                  ? alpha(theme.palette.primary.main, 0.8)
                                  : alpha(theme.palette.primary.main, 0.1)
                              }
                            }}
                          />
                        )
                      })}
                    </Box>
                  </Collapse>
                </Stack>
              </Grid>
            </>
          )}

          {/* Step 4: Upload de Imagens */}
          {activeStep === 4 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    color='text.primary'
                  >
                    📸 Imagens do Imóvel
                  </Typography>
                  <Chip
                    label={`${imagePreviews.length} / 12 (mínimo 1)`}
                    color={imagePreviews.length >= 6 ? 'success' : 'warning'}
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Divider sx={{ mb: 2, mt: 1, borderBottomWidth: 2 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant='outlined'
                  component='label'
                  startIcon={<CloudUploadRounded />}
                  fullWidth
                  size='medium'
                  sx={{
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  Adicionar Imagens (máx 5MB cada)
                  <input
                    type='file'
                    hidden
                    multiple
                    accept='image/*'
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  Adicione entre 1 e 12 imagens do imóvel. Clique em uma imagem
                  para torná-la principal.
                </Typography>
              </Grid>

              {imagePreviews.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity='info' sx={{ mb: 2 }}>
                    Clique em uma imagem para torná-la a principal do anúncio
                  </Alert>
                  <Grid container spacing={2}>
                    {imagePreviews.map((image, index) => (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                        <Card
                          elevation={image.is_main ? 8 : 2}
                          sx={{
                            position: 'relative',
                            border: image.is_main ? 3 : 0,
                            borderColor: 'primary.main',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                        >
                          <CardMedia
                            component='img'
                            height='150'
                            image={image.image}
                            alt={`Preview ${index + 1}`}
                            sx={{
                              cursor: 'pointer',
                              objectFit: 'cover'
                            }}
                            onClick={() => handleSetMainImage(index)}
                          />
                          {image.is_main && (
                            <Chip
                              label='Principal'
                              color='primary'
                              size='small'
                              icon={<CheckCircleRounded />}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                fontWeight: 'bold'
                              }}
                            />
                          )}
                          <CardActions
                            sx={{
                              justifyContent: 'center',
                              p: 1,
                              backgroundColor: alpha(
                                theme.palette.background.default,
                                0.9
                              )
                            }}
                          >
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => handleRemoveImage(index)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    0.1
                                  )
                                }
                              }}
                            >
                              <DeleteRounded fontSize='small' />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* Botões de Navegação */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }}
            >
              <Button
                type='button'
                variant='outlined'
                startIcon={<CancelRounded />}
                onClick={onCancel || (() => navigate('/dashboard'))}
                disabled={loading}
                size='large'
                sx={{ minWidth: 140 }}
              >
                Cancelar
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeStep > 0 && (
                  <Button
                    type='button'
                    variant='outlined'
                    onClick={handleBack}
                    disabled={loading}
                    size='large'
                    sx={{ minWidth: 120 }}
                  >
                    Voltar
                  </Button>
                )}

                {activeStep < steps.length - 1 ? (
                  <Button
                    type='button'
                    variant='contained'
                    onClick={handleNext}
                    disabled={loading}
                    size='large'
                    sx={{ minWidth: 140, fontWeight: 'bold' }}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    type='submit'
                    variant='contained'
                    startIcon={<SaveRounded />}
                    disabled={loading}
                    size='large'
                    sx={{
                      minWidth: 180,
                      fontWeight: 'bold',
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6
                      }
                    }}
                  >
                    {loading
                      ? 'Salvando...'
                      : isEditing
                        ? 'Atualizar Imóvel'
                        : 'Criar Imóvel'}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default PropertyForm
